// feature types
import type { JSONSchema7 } from 'json-schema'
import { HomepageJumpPages } from 'types/navigation'
import { RolodexEntryJson } from 'types/rolodex/rolodexEntries'
import {
  BusinessValueOption,
  DEFAULT_BUSINESS_VALUE,
} from 'utils/fde/aiaFinancialConsequenceHelpers'
import { parseBoolean, parseNumber, parseString } from 'utils/parsingHelpers'

import { CaseStatus, CaseType } from './geckoCase'
import { GeckoEntryBase } from './geckoEntry'

export const HealthPlanType = {
  Default: 'default',
  Mine: 'mine',
  Rooftops: 'rooftops',
} as const

export type HealthPlanType =
  (typeof HealthPlanType)[keyof typeof HealthPlanType]

export const AssetLifecycleConditionView = {
  Overview: 'overview',
  Risk: 'risk',
  NextRecommendedAction: 'next recommended action',
} as const

export type AssetLifecycleConditionView =
  (typeof AssetLifecycleConditionView)[keyof typeof AssetLifecycleConditionView]

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- this is the actual type
const isNullOrUndefined = (value: any) => {
  return value === null || value === undefined
}

export class GeckoSiteConfig extends GeckoEntryBase<
  {
    budget: number
    chartTimeframe: number
    site_level_criticality_mapping?: Record<string, string>
    yearlyBudgets: Record<string, number>
    businessValue?: BusinessValueOption
    healthPlanType?: HealthPlanType
    homepageJumpPages?: HomepageJumpPages[]
    includeDeficiencies?: boolean
    showDocumentOcrBoxes?: boolean
    includeRiskScenarios?: boolean
    caseConfig?: {
      caseStatuses: CaseStatus[]
      caseTypes: CaseType[]
      caseMetadataSchema?: JSONSchema7 // Used for defining the schema of case metadata for the site
    }
    showCapexOutlookOnSiteOverview?: boolean
    useAssetLifecycle?: boolean
    assetLifecycleConditionView?: AssetLifecycleConditionView
    showCorrosionMetrics?: boolean
    enableAlmRepairPlanning?: boolean
  },
  {
    site: string
    organization: string
  }
> {
  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        budget: parseNumber(json.data.budget) ?? 0,
        chartTimeframe: parseNumber(json.data.chart_timeframe) ?? 0,
        site_level_criticality_mapping: isNullOrUndefined(
          json.data.site_level_criticality_mapping
        )
          ? {
              '1': '1',
              '2': '2',
              '3': '3',
              '4': '4',
              '5': '5',
            }
          : (json.data.site_level_criticality_mapping as Record<
              string,
              string
            >),
        yearlyBudgets: isNullOrUndefined(json.data.yearly_budgets)
          ? {}
          : (json.data.yearly_budgets as Record<string, number>),
        businessValue: (parseString(json.data.business_value) ??
          DEFAULT_BUSINESS_VALUE) as BusinessValueOption,
        healthPlanType: (parseString(json.data.health_plan_type) ??
          HealthPlanType.Default) as HealthPlanType,
        homepageJumpPages: isNullOrUndefined(json.data.homepage_jump_pages)
          ? []
          : (json.data.homepage_jump_pages as HomepageJumpPages[]),
        includeDeficiencies: isNullOrUndefined(json.data.include_deficiencies)
          ? false
          : (json.data.include_deficiencies as boolean),
        showDocumentOcrBoxes:
          parseBoolean(json.data.show_document_ocr_boxes) ?? false,
        includeRiskScenarios: isNullOrUndefined(
          json.data.include_risk_scenarios
        )
          ? false
          : (json.data.include_risk_scenarios as boolean),
        caseConfig: isNullOrUndefined(json.data.case_config)
          ? undefined
          : (json.data.case_config as {
              caseStatuses: CaseStatus[]
              caseTypes: CaseType[]
              caseMetadataSchema?: JSONSchema7 // Used for defining the schema of case metadata for the site
            }),
        showCapexOutlookOnSiteOverview: isNullOrUndefined(
          json.data.show_capex_outlook_on_site_overview
        )
          ? false
          : (json.data.show_capex_outlook_on_site_overview as boolean),
        useAssetLifecycle: isNullOrUndefined(json.data.use_asset_lifecycle)
          ? false
          : (json.data.use_asset_lifecycle as boolean),
        assetLifecycleConditionView: isNullOrUndefined(
          json.data.asset_lifecycle_condition_view
        )
          ? AssetLifecycleConditionView.Overview
          : (json.data
              .asset_lifecycle_condition_view as AssetLifecycleConditionView),
        showCorrosionMetrics: isNullOrUndefined(
          json.data.show_corrosion_metrics
        )
          ? false
          : (json.data.show_corrosion_metrics as boolean),
        enableAlmRepairPlanning: isNullOrUndefined(
          json.data.enable_alm_repair_planning
        )
          ? false
          : (json.data.enable_alm_repair_planning as boolean),
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
      },
      {}
    )
  }

  /**
   * A simple factory method which creates a report from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoSiteConfig(json)
  }
}
